/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { catchError, delayWhen, distinctUntilChanged, filter, finalize, map, retryWhen } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { NotAvailableReasonDetails, NotAvailableReasonRequest } from '../models/not-available-reason';
import { AppRoutes } from '../enums/app-routes';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { SettingsService } from './settings.service';
import { MessageService } from './message.service';
import { AuthService } from './auth.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class NotAvailableReasonService {
  private notAvailableReasonsEnabled: boolean = false;
  private lastNewRequest: NotAvailableReasonRequest = null;

  public openRequests$ = new BehaviorSubject<NotAvailableReasonRequest[]>(null);
  public openRequestsMessage$ = new BehaviorSubject<string>('');

  public constructor(
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly translateService: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly router: Router,
    private readonly loggingService: LoggingService,
  ) {
    this.settingsService.settings$.subscribe((settings) => {
      this.notAvailableReasonsEnabled = settings.notAvailableReasons && settings.notAvailableReasons.length > 0;
      this.getOpenRequests();
    });

    this.openRequests$
      .pipe(
        distinctUntilChanged((x, y) => isEqual(x, y)),
        map((x) => (x ? x.reverse() : x)),
        filter((x) => (x ? x[0]?.requestId !== this.lastNewRequest?.requestId : null)),
      )
      .subscribe((requests) => {
        this.lastNewRequest = requests[0];
        if (this.router.url.includes(AppRoutes.home)) {
          this.router.navigate([AppRoutes.notAvailableReason]);
          MessageService.SendBringToFrontMessage();
        }
      });

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => this.translateOpenRequestMessage());
  }

  public cancel(): void {
    this.openRequests$.next(null);
    this.openRequestsMessage$.next('');
    this.getOpenRequests();
  }

  public getOpenRequests(): void {
    if (this.notAvailableReasonsEnabled) {
      this.apiService
        .getNotAvailableReasonRequests()
        .pipe(
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          retryWhen(delayWhen((err, i) => (i < 1 && this.authService.authenticated$.value ? timer(1000) : throwError(err)))),
          catchError((err) => {
            this.loggingService.Info(`Request failed: ${JSON.stringify(err)}`);
            return of(null);
          }),
          filter((x) => !!x?.requests),
        )
        .subscribe((requests: NotAvailableReasonDetails) => {
          this.openRequests$.next(requests.requests);
          this.translateOpenRequestMessage();
        });
    } else {
      this.openRequests$.next(null);
      this.openRequestsMessage$.next('');
    }
  }

  public translateOpenRequestMessage(): void {
    const number = this.openRequests$.value?.length;
    const message = number ? this.translateService.instant(TranslationKeys.NotAvailableReasonOpenRequests, { number }) : '';
    this.openRequestsMessage$.next(message);
  }

  public sendNotAvailableReason(requestId: string, reasonId: string, comment: string): Observable<void> {
    return this.apiService
      .submitNotAvailableReason({
        requestId,
        reasonId,
        comment,
      })
      .pipe(
        catchError((err) => {
          this.loggingService.Info(`Request failed: ${JSON.stringify(err)}`);
          this.settingsService.pollingAction();
          return of(null);
        }),
        finalize(() => {
          this.getOpenRequests();
        }),
      );
  }
}
